import type Canvas from '../Canvas';
import type ShapeImage from '../../shapes/ShapeImage';
import CropOverlay from './CropOverlay';
import CropShape from './CropShape';
import type Shape from '../Shape';
import { hasDataStoreRecordBehaviors } from '../guards';

class Crop {
  image: Shape<ShapeImage>;
  canvas: Canvas;
  cropShape: CropShape;
  overlay: CropOverlay;

  constructor(image: Shape<ShapeImage>) {
    this.image = image;
    this.image.evented = false;
    this.image.selectable = false;
    this.canvas = image.controller.canvas;

    this.overlay = new CropOverlay(image);
    this.cropShape = new CropShape(image);

    this.canvas.addObjects([this.overlay], { where: 'top' });
    this.canvas
      .addObjects([this.cropShape], { where: 'top' })
      .setActiveObject(this.cropShape)
      .requestRenderAll();

    // Cleanup when a collaborator removes the image being actively cropped.
    this.image.on('removed', () => this.destroy());
    this.cropShape.on('scaling', () => {
      this.overlay.updateClipPath(this.cropShape);
    });

    this.cropShape.on('moving', () => {
      this.overlay.updateClipPath(this.cropShape);
    });
  }

  destroy() {
    const { image, canvas, overlay, cropShape } = this;

    image.evented = true;
    image.selectable = true;
    overlay.deleted();
    canvas.remove(overlay);
    canvas.remove(cropShape);
  }

  wasClicked(target?: fabric.Object) {
    return (
      hasDataStoreRecordBehaviors(target) &&
      this.cropShape.uniqueId() === target?.uniqueId()
    );
  }

  get values() {
    const { cropShape } = this;

    return {
      left: cropShape.left,
      top: cropShape.top,
      width: cropShape.width,
      height: cropShape.height,
      scaleX: cropShape.scaleX,
      scaleY: cropShape.scaleY,
    };
  }
}

export default Crop;
